// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { Box, Button, Flex, Divider } from '@mantine/core';

import { OptionalForm } from 'components/Mantine/Modals/WrappedModal';
import { isNotNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';

import type { PopoverContentProps } from './types';

type Props = Pick<
  PopoverContentProps,
  'children' | 'onConfirm' | 'onClose' | 'withForm' | 'disabled' | 'confirmationText' | 'confirmationCount'
>;

const BodyWithAutoFocus = ({ children }: Pick<Props, 'children'>) => {
  // On mobile, tapping a field opens the on-screen keyboard, which shrinks the viewport and
  // re-caps the popover height (via the `size` middleware) after the browser has already
  // scrolled the field into view — leaving the just-tapped field off-screen. Re-scroll the
  // focused field into view whenever the scroll body's size changes.
  const observeScrollBody = useCallback((body: HTMLDivElement) => {
    const observer = new ResizeObserver(() => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && body.contains(active)) {
        active.scrollIntoView({ block: 'nearest' });
      }
    });
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  return (
    /* Scrollable body: shrinks to the available height and scrolls internally,
        keeping the footer below always reachable. */
    <Box ref={observeScrollBody} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {children}
    </Box>
  );
};

/**
 * The shared body and Cancel/Confirm footer of a `WrappedPopover`, optionally
 * wrapped in a `<form>` when the popover submits through `formSubmit`.
 */
export const PopoverConfirmation = ({
  children,
  onConfirm,
  onClose,
  withForm,
  disabled,
  confirmationText,
  confirmationCount,
}: Props) => {
  const isMobile = useIsMobile();

  return (
    <OptionalForm
      onFormSubmit={
        withForm
          ? (e) => {
              // stopPropagation so the submit never bubbles (through a Portal, React events
              // still bubble up the component tree) into a `<form>` a caller renders around us.
              e?.preventDefault();
              e?.stopPropagation();
              void onConfirm(e);
            }
          : undefined
      }
      formStyle={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <BodyWithAutoFocus>{children}</BodyWithAutoFocus>

      {isMobile && <Divider mt="sm" />}

      <Flex gap="sm" mt="sm" style={{ flexShrink: 0 }}>
        <Button fullWidth onClick={onClose} variant="light" size={isMobile ? 'sm' : 'xs'}>
          Cancel
        </Button>
        <Button
          fullWidth
          {...(withForm
            ? { type: 'submit' as const }
            : {
                onClick: () => {
                  void onConfirm();
                },
              })}
          disabled={disabled}
          size={isMobile ? 'sm' : 'xs'}
        >
          {confirmationText}
          {isNotNil(confirmationCount) && <span style={{ fontWeight: 400, marginLeft: 4 }}>({confirmationCount})</span>}
        </Button>
      </Flex>
    </OptionalForm>
  );
};
